import { GlitchWord } from "./GlitchWord"
import { useGlitchEnabled } from "../lib/useGlitchEnabled"
import type { GlitchColorBase } from "../lib/glitch"

export type TerminalLine = {
    prefix: string
    value: string
    prefixColor?: GlitchColorBase | "none" // map to text coloring, optional 
    valueColorBase?: GlitchColorBase        // color for glitched value (hot word)
    glitch?: boolean            // if true, value is a GlitchWord at ambient intensity
    opacity?: number            // default 1, use 0.3–0.5 for ghost lines
}

export interface GlitchTerminalProps {
    lines: TerminalLine[]
    intensity: number           // global or local intensity driving any glitched lines
    className?: string          // optional override spacing / overall styling
    ambient?: boolean           // determines if we use standard text or dimmed default
    externalTrigger?: number    // for coordinated burst animations
    duration?: number           // duration of the external trigger burst
}

const colorMap: Record<string, string> = {
    luster: "text-luster-400",
    teal: "text-teal-400",
    red: "text-red-400",
    amber: "text-amber-400",
    violet: "text-violet-400",
    mixed: "text-white/70", // default fallback
    peach: "text-orange-300",
    none: "text-white/50",
}

export function GlitchTerminal({
    lines,
    intensity,
    className = "",
    ambient = true,
    externalTrigger = 0,
    duration = 600
}: GlitchTerminalProps) {
    const [glitchEnabled] = useGlitchEnabled()

    return (
        <div className={`font-mono text-[11px] space-y-1.5 ${className}`}>
            {lines.map((line, idx) => {
                const lineOpacity = line.opacity ?? (ambient ? 0.7 : 1)
                const prefixClass = line.prefixColor && colorMap[line.prefixColor]
                    ? colorMap[line.prefixColor]
                    : "text-white/40"

                return (
                    <p key={idx} style={{ opacity: lineOpacity }} className="flex">
                        <span className={`${prefixClass} w-24 shrink-0`}>
                            {line.prefix}
                        </span>
                        <span className={ambient && !line.glitch ? "text-white/60" : "text-white/90"}>
                            {line.glitch && glitchEnabled ? (
                                <GlitchWord
                                    word={line.value}
                                    intensity={intensity}
                                    colorBase={line.valueColorBase ?? "mixed"}
                                    externalTrigger={externalTrigger}
                                    duration={duration}
                                />
                            ) : (
                                <span>{line.value}</span>
                            )}
                        </span>
                    </p>
                )
            })}
        </div>
    )
}
